import axios from "axios";
import { API_BASE_URL, NODE_API_URL, SHOPIFY_API_KEY } from "../config/env";
import { APP_DISPLAY_NAME } from "../config/appBranding";
import { getSessionToken, authenticatedFetch } from "@shopify/app-bridge-utils";
import createApp from "@shopify/app-bridge";

const getAppBridgeInstance = (app = null) => {
  if (app) return app;
  if (typeof window === "undefined") return null;
  if (window.__shopifyAppBridgeInstance) return window.__shopifyAppBridgeInstance;
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get("host");
    const apiKey = SHOPIFY_API_KEY;
    if (apiKey && host) {
      const appInstance = createApp({
        apiKey: apiKey,
        host: host,
        forceRedirect: process.env.NODE_ENV === "production",
      });
      window.__shopifyAppBridgeInstance = appInstance;
      return appInstance;
    }
  } catch (error) {
    console.error("Failed to create App Bridge instance:", error);
  }
  return null;
};

const SESSION_TOKEN_TIMEOUT_MS = 12000;

function isSessionExpired401(status, raw) {
  if (status !== 401 || !raw || typeof raw !== "object") return false;
  const msg = String(raw.message || raw.error || "").toLowerCase();
  return msg.includes("session expired") || msg.includes("jwt expired") || msg.includes("token expired");
}

/** Avoid showing long internal hints (secrets, Partners) in banners / toasts. */
function sanitizeApiErrorForUi(status, message) {
  const s = String(message || "").trim();
  if (!s) return s;
  const lower = s.toLowerCase();
  const looksInternal =
    lower.includes("shopify_secret") ||
    lower.includes("shopify partners") ||
    lower.includes("embedded). if this keeps happening");
  if (looksInternal || (status === 401 && s.length > 80)) {
    return "Invalid session";
  }
  if (lower.includes("could not get a session token in time")) {
    return "Could not verify session. Open the app from Shopify Admin and try again.";
  }
  return s;
}

function isNonInstallEndpoint(endpoint) {
  const clean = String(endpoint || "").replace(/^\//, "");
  return clean !== "install";
}

async function apiCallOnce(endpoint, method, data, app, callOptions) {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  const isInstallEndpoint = cleanEndpoint === "install";
  const appBridge = isInstallEndpoint ? null : getAppBridgeInstance(app);

  let sessionToken = null;
  if (appBridge) {
    try {
      sessionToken = await Promise.race([
        getSessionToken(appBridge),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("__SESSION_TOKEN_TIMEOUT__")), SESSION_TOKEN_TIMEOUT_MS),
        ),
      ]);
    } catch (tokenError) {
      if (tokenError && tokenError.message === "__SESSION_TOKEN_TIMEOUT__") {
        throw new Error("Could not verify session. Open the app from Shopify Admin and try again.");
      }
      console.error("Failed to get session token:", tokenError);
    }
  }

  let cleanBaseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  cleanBaseUrl = isInstallEndpoint ? NODE_API_URL : cleanBaseUrl;
  const fullUrl = `${cleanBaseUrl}/${cleanEndpoint}`;

  const headers = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  };
  if (sessionToken) {
    headers.authorization = `Bearer ${sessionToken}`;
  }

  const m = String(method || "GET").toUpperCase();
  const axiosConfig = {
    method: m,
    url: fullUrl,
    headers,
    timeout: typeof callOptions.timeout === "number" ? callOptions.timeout : 10000,
  };
  if (m !== "GET" && m !== "HEAD" && data != null) {
    axiosConfig.data = data;
  }

  return axios(axiosConfig);
}

/** Coalesce parallel/repeated identical GET/HEAD calls (e.g. React Strict Mode). */
const GET_DEDUPE_WINDOW_MS = 800;
const inflightGetRequests = new Map();
const recentGetResults = new Map();

function apiCallDedupeKey(method, endpoint) {
  const m = String(method || "GET").toUpperCase();
  if (m !== "GET" && m !== "HEAD") return null;
  const clean = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  return `${m}:${clean}`;
}

async function executeApiCall(endpoint, method = "GET", data = null, app = null, callOptions = {}) {
  try {
    let response;
    try {
      response = await apiCallOnce(endpoint, method, data, app, callOptions);
    } catch (firstErr) {
      const st = firstErr.response?.status;
      const raw = firstErr.response?.data;
      const bridge = getAppBridgeInstance(app);
      if (st === 401 && isSessionExpired401(st, raw) && bridge && isNonInstallEndpoint(endpoint)) {
        response = await apiCallOnce(endpoint, method, data, app, callOptions);
      } else {
        throw firstErr;
      }
    }
    return response.data;
  } catch (error) {
    console.error("API call failed:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });

    const raw = error.response?.data;
    if (raw && typeof raw === "string" && raw.includes("<!DOCTYPE")) {
      throw new Error(`API endpoint not found. Check backend URL and route: ${error.config?.url}`);
    }
    if (typeof raw === "string") {
      throw new Error(sanitizeApiErrorForUi(error.response?.status, raw));
    }
    if (raw && typeof raw === "object") {
      const status = error.response?.status;
      const rawMsg =
        raw.message ||
        (typeof raw.error === "string" ? raw.error : raw.error?.message) ||
        (Array.isArray(raw.errors) && raw.errors[0]?.message) ||
        `Request failed${status ? ` (${status})` : ""}`;
      throw new Error(sanitizeApiErrorForUi(status, rawMsg));
    }
    throw new Error(
      sanitizeApiErrorForUi(error.response?.status, error.message || "Request failed"),
    );
  }
}

const apiCall = async (endpoint, method = "GET", data = null, app = null, callOptions = {}) => {
  if (callOptions.dedupe === false) {
    return executeApiCall(endpoint, method, data, app, callOptions);
  }
  const key = apiCallDedupeKey(method, endpoint);
  if (!key) {
    return executeApiCall(endpoint, method, data, app, callOptions);
  }

  const inflight = inflightGetRequests.get(key);
  if (inflight) {
    return inflight;
  }

  const cached = recentGetResults.get(key);
  if (cached && Date.now() - cached.at < GET_DEDUPE_WINDOW_MS) {
    return cached.value;
  }
  if (cached) {
    recentGetResults.delete(key);
  }

  const promise = executeApiCall(endpoint, method, data, app, callOptions)
    .then((data) => {
      recentGetResults.set(key, { at: Date.now(), value: data });
      return data;
    })
    .finally(() => {
      inflightGetRequests.delete(key);
    });
  inflightGetRequests.set(key, promise);
  return promise;
};

const SHOPIFY_ADMIN_GRAPHQL_URL = "shopify:admin/api/graphql.json";

/** Never use global fetch() for shopify: URLs — browsers reject that scheme. */
function hasShopifyBridgeFetch() {
  return typeof window?.shopify?.fetch === "function";
}

/**
 * Admin GraphQL from embedded app via App Bridge only (no bare fetch, no Node proxy).
 * 1) authenticatedFetch(createApp) — @shopify/app-bridge-utils
 * 2) window.shopify.fetch — CDN app-bridge.js (App Bridge 4)
 */
async function graphqlCall(query) {
  if (typeof window === "undefined") {
    return { errors: [{ message: "App Bridge is not available" }] };
  }

  const fetchOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(query),
    cache: "no-store",
  };

  const app = getAppBridgeInstance();
  if (app) {
    try {
      const fetchFn = authenticatedFetch(app);
      const res = await fetchFn(SHOPIFY_ADMIN_GRAPHQL_URL, fetchOptions);
      const json = await res.json();
      if (json && (json.data !== undefined || json.errors)) {
        return json;
      }
    } catch (error) {
      console.warn("[graphqlCall] authenticatedFetch:", error?.message || error);
    }
  }

  if (hasShopifyBridgeFetch()) {
    try {
      const res = await window.shopify.fetch(SHOPIFY_ADMIN_GRAPHQL_URL, fetchOptions);
      const json = await res.json();
      if (json && (json.data !== undefined || json.errors)) {
        return json;
      }
    } catch (error) {
      console.warn("[graphqlCall] shopify.fetch:", error?.message || error);
    }
  }

  const host = new URLSearchParams(window.location.search).get("host");
  return {
    errors: [
      {
        message: host
          ? `App Bridge GraphQL failed. Reload from Shopify Admin → Apps → ${APP_DISPLAY_NAME}. Ensure shopify.app.toml has [access.admin] embedded_app_direct_api_access = true and run shopify app deploy.`
          : "Open this app from Shopify Admin (URL must include ?shop=…&host=…). Direct browser tabs cannot call Admin GraphQL.",
      },
    ],
  };
}

/**
 * Admin GraphQL via Node proxy (session JWT → shop access token).
 * Use for theme/embed checks — avoids browser fetch("shopify:admin/...") failures.
 */
async function adminGraphqlProxy(queryPayload, callOptions = {}) {
  if (!queryPayload || typeof queryPayload.query !== "string") {
    return { errors: [{ message: "Invalid GraphQL payload" }] };
  }
  try {
    return await apiCall("admin/graphql", "POST", queryPayload, null, callOptions);
  } catch (error) {
    const d = error?.response?.data;
    if (d && typeof d === "object" && (Array.isArray(d.errors) || d.data !== undefined)) {
      return d;
    }
    const msg =
      typeof error === "string"
        ? error
        : error?.message || error?.response?.data?.message || "GraphQL proxy request failed";
    return { errors: [{ message: msg }] };
  }
}

export { apiCall, graphqlCall, adminGraphqlProxy };
